import { useParams, Link } from 'react-router-dom'
import { mockVendors } from '../data/mockVendors'
import { mockReviews } from '../data/mockReviews'
import VendorTypeBadges from '../components/VendorTypeBadges'

export default function VendorProfile() {
  const { vendorId } = useParams<{ vendorId: string }>()
  
  const vendor = mockVendors.find(v => v.id === vendorId)
  const vendorReviews = mockReviews.filter(r => r.vendorId === vendorId)
  
  if (!vendor) {
    return (
      <div className="vendor-profile-not-found">
        <h1>Vendor Not Found</h1>
        <p>The vendor you're looking for doesn't exist.</p>
        <Link to="/vendors" className="back-to-vendors">← Back to Vendor Directory</Link>
      </div>
    )
  }
  
  return (
    <div className="vendor-profile">
      <div className="vendor-profile-header">
        <div className="vendor-basic-info">
          <h1 className="vendor-name">
            {vendor.name}
            {vendor.verified && <span className="verified-badge">✓ Verified</span>}
          </h1>
          
          <VendorTypeBadges specialties={vendor.specialties} size="medium" />
          
          <div className="vendor-meta">
            <p className="vendor-location">📍 {vendor.location}</p>
            <p className="vendor-founded">🏢 Founded: {vendor.founded}</p>
          </div>
          
          <div className="vendor-rating-summary">
            <div className="rating-display">
              <span className="rating-score">{vendor.rating}</span>
              <span className="rating-stars">{'★'.repeat(Math.floor(vendor.rating))}{'☆'.repeat(5-Math.floor(vendor.rating))}</span>
              <span className="review-count">({vendor.reviewCount} reviews)</span>
            </div>
          </div>
        </div>
        
        <div className="vendor-actions">
          <a 
            href={vendor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Visit Website →
          </a>
          <Link to={`/reviews?vendor=${vendor.id}`} className="btn btn-secondary">
            View All Reviews
          </Link>
        </div>
      </div>
      
      <div className="vendor-profile-content">
        <div className="vendor-description-section">
          <h2>About {vendor.name}</h2>
          <p className="vendor-description">{vendor.description}</p>
        </div>
        
        <div className="vendor-details-section">
          <h2>Vendor Details</h2>
          <div className="details-grid">
            <div className="detail-item">
              <strong>Specialties:</strong>
              <div className="specialties-list">
                {vendor.specialties.map((specialty, index) => (
                  <span key={index} className="specialty-tag">{specialty}</span>
                ))}
              </div>
            </div>
            
            <div className="detail-item">
              <strong>Shipping Regions:</strong>
              <span>{vendor.shippingRegions.join(', ')}</span>
            </div>
            
            <div className="detail-item">
              <strong>Payment Methods:</strong>
              <span>{vendor.paymentMethods.join(', ')}</span>
            </div>
            
            {vendor.socialMedia && (
              <div className="detail-item">
                <strong>Social Media:</strong>
                <div className="social-links">
                  {vendor.socialMedia.reddit && (
                    <a href={`https://reddit.com/${vendor.socialMedia.reddit}`} target="_blank" rel="noopener noreferrer">
                      Reddit
                    </a>
                  )}
                  {vendor.socialMedia.instagram && (
                    <a href={`https://instagram.com/${vendor.socialMedia.instagram}`} target="_blank" rel="noopener noreferrer">
                      Instagram
                    </a>
                  )}
                  {vendor.socialMedia.facebook && (
                    <a href={`https://facebook.com/${vendor.socialMedia.facebook}`} target="_blank" rel="noopener noreferrer">
                      Facebook
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="recent-reviews-section">
          <h2>Recent Reviews</h2>
          {vendorReviews.length > 0 ? (
            <div className="reviews-preview">
              {vendorReviews.slice(0, 3).map((review) => (
                <div key={review.id} className="review-preview-card">
                  <div className="review-header">
                    <div className="review-rating">
                      <span className="rating-stars">{'★'.repeat(review.rating)}</span>
                      <span className="rating-score">{review.rating}/5</span>
                    </div>
                    <span className="review-date">
                      {review.dateCreated.toLocaleDateString()}
                    </span>
                  </div>
                  <h4 className="review-title">{review.title}</h4>
                  <p className="review-content">{review.content.slice(0, 200)}...</p>
                  <div className="review-footer">
                    <span className="reviewer">By {review.userName}</span>
                    {review.verified && <span className="verified-purchase">✓ Verified Purchase</span>}
                  </div>
                </div>
              ))}
              
              {vendorReviews.length > 3 && (
                <Link to={`/reviews?vendor=${vendor.id}`} className="view-all-reviews">
                  View All {vendorReviews.length} Reviews →
                </Link>
              )}
            </div>
          ) : (
            <p className="no-reviews">No reviews yet for this vendor.</p>
          )}
        </div>
      </div>
      
      <div className="vendor-profile-footer">
        <Link to="/vendors" className="back-to-vendors">← Back to Vendor Directory</Link>
      </div>
    </div>
  )
}